import { COMMA, ENTER, SEMICOLON } from '@angular/cdk/keycodes';
import { FlatTreeControl } from '@angular/cdk/tree';
import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteOrigin,
  MatAutocompleteTrigger,
  MatOption as MatOption_1
} from '@angular/material/autocomplete';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatChipGrid,
  MatChipInput,
  MatChipRemove,
  MatChipRow
} from '@angular/material/chips';
import { MatOption } from '@angular/material/core';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import {
  MatError,
  MatFormField,
  MatHint,
  MatInput
} from '@angular/material/input';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import {
  MatTree,
  MatTreeFlatDataSource,
  MatTreeFlattener,
  MatTreeNode,
  MatTreeNodeDef,
  MatTreeNodePadding,
  MatTreeNodeToggle
} from '@angular/material/tree';
import {
  Observable,
  debounceTime,
  defaultIfEmpty,
  forkJoin,
  map,
  mergeMap,
  of,
  share,
  take
} from 'rxjs';
import { Group } from '../../../../models/general.models';
import { ChildProject, Project } from '../../../../models/project.module';
import { AreaService } from '../../../../services/area.service';
import { GroupService } from '../../../../services/group.service';
import { NewProjectService } from '../../../../services/newProject.service';
import { ProjectService } from '../../../../services/project.service';
import { ToastNotificationService } from '../../../../services/toast-notification.service';
import { MapCommunicatorService } from '../../map-communicator.service';

interface FlatProject extends Omit<Project, 'children'> {
  level: number;
  expandable: boolean;
  children: Array<FlatProject>;
}

@Component({
  selector: 'frmg-save-area-dialog',
  templateUrl: './save-area-dialog.component.html',
  styleUrls: ['./save-area-dialog.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatError,
    MatSelect,
    MatSelectTrigger,
    MatOption_1,
    MatProgressBar,
    MatTree,
    MatTreeNodeDef,
    MatTreeNode,
    MatTreeNodePadding,
    NgClass,
    MatIconButton,
    MatTreeNodeToggle,
    MatIcon,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatAutocompleteTrigger,
    MatChipInput,
    MatAutocompleteOrigin,
    MatAutocomplete,
    MatHint,
    MatButton,
    MatDialogClose,
    AsyncPipe
  ]
})
export class SaveAreaDialogComponent implements OnInit {
  public saveAreaGroup: FormGroup;
  public separatorKeysCodes = [ENTER, COMMA, SEMICOLON];
  public tagsList: Observable<Array<Group>>;
  public folders: Array<Project> = [];
  public isLoading = false;
  public duplicated = false;
  public projectsLoaded = false;

  private _transformer = (node: FlatProject, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      level,
      ...node
    };
  };
  public treeControl = new FlatTreeControl<FlatProject>(
    (node) => node.level,
    (node) => node.expandable
  );

  public treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  public dataSource = new MatTreeFlatDataSource(
    this.treeControl,
    this.treeFlattener
  );

  @ViewChild('tagInput') tagInput: ElementRef<HTMLInputElement>;
  @ViewChild('tagsChipList') tagsChipList: MatAutocomplete;
  @ViewChild('tagsChipList', { static: true }) chipList: MatChipGrid;
  @ViewChild('selectPanel') selectPanel: MatSelect;
  @ViewChild('folderOption') folderOption: MatOption;
  public isEdit: boolean = false;
  public selectedFolder;

  constructor(
    private dialogRef: MatDialogRef<SaveAreaDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { project: Project; layer: any },
    private fb: FormBuilder,
    private groupService: GroupService,
    private projectService: ProjectService,
    private newProjectService: NewProjectService,
    private toastNotificationService: ToastNotificationService,
    private mapCommunicatorService: MapCommunicatorService,
    private areaService: AreaService,
    private cd: ChangeDetectorRef
  ) {
    this.isEdit = !!this.data.layer.geometry;
    if (this.data.project && this.data.project.path !== 'root')
      this.selectedFolder = this.data.project;
    this.saveAreaGroup = this.fb.group({
      name: [
        this.isEdit ? this.data.layer.properties?.name : '',
        [Validators.required]
      ],
      folder: [
        this.data.project || null,
        this.data.project ? [Validators.required] : []
      ],
      tags: [
        this.isEdit ? this.data.layer.properties?.groups : [],
        [Validators.maxLength(10)]
      ],
      tagInput: ['']
    });
  }

  ngOnInit(): void {
    this.tagsList = this.saveAreaGroup.get('tagInput')!.valueChanges.pipe(
      debounceTime(500),
      mergeMap((tag) => {
        if (typeof tag !== 'string') {
          tag = '';
        }
        return this.groupService.searchGroup(tag);
      }),
      share()
    );

    this.tagsList.subscribe((groups) => {
      this.duplicated = groups.some(
        (group) => group.name === this.saveAreaGroup.get('tagInput').value
      );
    });

    const currentProject = this.data.project;
    if (currentProject) {
      if (currentProject.is_root || currentProject.path === 'root') {
        this.loadRootProjects();
      } else {
        this.loadNestedProjectHierarchy(currentProject);
      }
    } else {
      this.loadRootProjects();
    }
  }

  private findNode(nodes) {
    for (const node of nodes) {
      if (node.id === this.selectedFolder.id) return node;

      if (node.rows) {
        const match = this.findNode(node.children);
        if (match) return match;
      }
    }
  }

  expandParents(node) {
    const parent = this.getParent(node);
    this.treeControl.expand(parent);

    if (parent && parent.level > 0) {
      this.expandParents(parent);
    }
  }

  private getParent(node) {
    const { treeControl } = this;
    const currentLevel = treeControl.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = treeControl.dataNodes[i];

      if (treeControl.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  public onTreeNodeSelect(node: FlatProject) {
    this.selectedFolder = node;
    this.cd.detectChanges();
    this.saveAreaGroup.get('folder').setValue(node);
    this.selectPanel.toggle();
    // this.selectPanel.options.changes.subscribe(update=>{
    //   this.selectPanel.options.get(0).value.id === this.folderOption.value.id && this.folderOption.select(true);
    //   this.selectPanel.toggle();
    //   this.cd.detectChanges();
    // })
  }

  public compareObjects(
    o1: { id: string; name: string },
    o2: { id: string; name: string }
  ): boolean {
    return o1?.name === o2?.name && o1?.id === o2?.id;
  }

  private createRecursiveTreeRequests(
    projects: ChildProject[]
  ): Observable<Project[]> {
    projects = projects.filter((project) => !project.is_archived);
    return forkJoin(
      projects.map((project) => this.projectService.getProjectById(project.id))
    ).pipe(
      mergeMap((projects) =>
        forkJoin(
          projects.map((project) => {
            if (!!project.children && project.children.length) {
              return this.createRecursiveTreeRequests(project.children).pipe(
                map((projects) => {
                  // @ts-ignore
                  project.children = projects;
                  return project;
                })
              );
            } else {
              return of(project);
            }
          })
        )
      )
    );
  }

  hasChild = (_: number, node: FlatProject) => node.expandable;

  public closeDialog(result: Record<string, unknown>): void {
    this.dialogRef.close(result);
  }

  public save(): void {
    this.isLoading = true;
    const formValues = this.saveAreaGroup.value;
    const tags: Group[] = formValues.tags;
    const nonExistingTags = tags.filter((group) => group.id === undefined);
    const createTagsRequests: Array<Observable<Group>> = [];
    if (nonExistingTags.length) {
      nonExistingTags.forEach((group) =>
        createTagsRequests.push(this.groupService.createGroup(group))
      );
    }

    forkJoin(createTagsRequests)
      .pipe(
        defaultIfEmpty([]),
        map((groupResponse) => {
          return tags.map((tag) => {
            if (tag.id !== undefined) {
              return tag;
            } else {
              return (
                groupResponse.find((group) => group.name === tag.name) ||
                groupResponse[0]
              );
            }
          });
        }),
        mergeMap((completeTags) => {
          if (this.isEdit) {
            const projectId =
              this.data.project?.project_id || formValues.folder?.project_id;
            return this.areaService.changeArea(projectId, this.data.layer, {
              name: formValues.name,
              groups: completeTags
            });
          } else {
            const selectedFolder = formValues.folder;
            const rootProjectId =
              this.data.project?.project_id || selectedFolder.project_id;

            const area = {
              name: formValues.name,
              project_id: rootProjectId,
              folder_id: selectedFolder.id,
              geometry: this.data.layer,
              properties: {
                groups: completeTags,
                name: formValues.name
              }
            };
            return this.areaService.createArea(area);
          }
        })
      )
      .subscribe({
        next: (resp) => {
          if (
            this.data.project &&
            this.data.project.id == formValues.folder.id
          ) {
            this.areaService.areasList
              .pipe(debounceTime(0), take(1))
              .subscribe({
                next: (currentAreas) => {
                  if (this.isEdit) {
                    const index = currentAreas.findIndex(
                      (area) => area.properties.id === resp.properties.id
                    );
                    if (index !== -1) {
                      currentAreas[index] = resp;
                      this.areaService.updateAreas([...currentAreas]);
                    }
                  } else {
                    this.areaService.updateAreas([...currentAreas, resp]);
                    this.mapCommunicatorService.mapCommunicator.next(
                      'added_new_area'
                    );
                  }
                },
                error: (err) => {
                  console.error('Error updating areas list:', err);
                }
              });
          }

          this.isLoading = false;
          this.toastNotificationService.showNotification({
            message: 'Saved successfully',
            type: 'success'
          });

          setTimeout(() => {
            this.mapCommunicatorService.mapCommunicator.next(
              'refetch_areas_for_map'
            );
          }, 100);

          this.closeDialog({ success: true });
        },
        error: (error) => {
          this.isLoading = false;
          this.toastNotificationService.showNotification({
            message: Object.values(error.error)[0][0],
            type: 'error'
          });
        }
      });
  }

  public removeTag(tag: Group): void {
    const tags: Group[] = this.saveAreaGroup.get('tags')?.value;
    const index = tags.findIndex((group) => group.name === tag.name);
    if (index >= 0) {
      tags.splice(index, 1);
      this.saveAreaGroup.get('tags')?.setValue(tags, { emitEvent: true });
    }
  }

  public addTag(value): void {
    let tags: Group[] = this.saveAreaGroup.get('tags')?.value;
    if (!tags || tags.findIndex((tag) => value.name === tag.name) === -1) {
      if (!tags) {
        tags = [];
      }
      tags.push(value);
      this.saveAreaGroup.get('tags')?.setValue(tags, { emitEvent: true });
    }
    this.tagInput.nativeElement.value = '';
    this.saveAreaGroup.get('tagInput')?.setValue('', { emitEvent: true });
  }

  public displayName(project: Project) {
    return project.name || '';
  }

  private loadRootProjects(): void {
    forkJoin({
      root: this.newProjectService.getProjectsByPath('', 'root', 'name:asc'),
      shared: this.newProjectService.getProjectsByPath(
        '',
        'shared',
        'name:asc'
      )
    })
      .pipe(
        map(({ root, shared }) => {
          return [...root, ...shared].filter(
            (project: Project) => !project.is_archived
          );
        }),
        mergeMap((allProjects) => {
          if (allProjects.length === 0) {
            return of([]);
          }
          return this.createRecursiveTreeRequests(
            allProjects as unknown as ChildProject[]
          );
        })
      )
      .subscribe({
        next: (children) => {
          this.dataSource.data = children as any;
          this.projectsLoaded = true;

          if (
            !this.selectedFolder &&
            this.data?.project &&
            this.data.project.path !== 'root'
          ) {
            this.selectedFolder = this.data.project;
            this.saveAreaGroup.get('folder')?.setValue(this.selectedFolder);
          }

          if (!this.selectedFolder && children && children.length > 0) {
            this.selectedFolder = children[0];
            this.saveAreaGroup
              .get('folder')
              ?.setValidators([Validators.required]);
            this.saveAreaGroup.get('folder')?.setValue(this.selectedFolder);
            this.saveAreaGroup.get('folder')?.updateValueAndValidity();
          }

          if (this.selectedFolder) {
            const node = this.findNode(this.treeControl.dataNodes);
            if (node) {
              this.expandParents(node);
            }
          }

          this.cd.detectChanges();
        },
        error: (error) => {
          console.error('Error loading root projects:', error);
          this.dataSource.data = [];
          this.projectsLoaded = true;
          this.toastNotificationService.showNotification({
            message: 'Failed to load projects. Please try again.',
            type: 'error'
          });
          this.cd.detectChanges();
        }
      });
  }

  private loadNestedProjectHierarchy(currentProject: Project): void {
    this.projectService
      .getProjectById(currentProject.id)
      .pipe(
        mergeMap((fullProject) => {
          return this.createRecursiveTreeRequests(
            fullProject.children || []
          ).pipe(map((children) => ({ fullProject, children })));
        }),
        take(1)
      )
      .subscribe({
        next: ({ fullProject, children }) => {
          const node = { ...fullProject, children } as any;
          this.dataSource.data = [node];
          this.projectsLoaded = true;

          this.selectedFolder = fullProject;
          this.saveAreaGroup.get('folder')?.setValue(fullProject);
          this.saveAreaGroup
            .get('folder')
            ?.setValidators([Validators.required]);
          this.saveAreaGroup.get('folder')?.updateValueAndValidity();

          const selectedNode = this.findNode(this.treeControl.dataNodes);
          if (selectedNode) {
            this.expandParents(selectedNode);
          }

          this.cd.detectChanges();
        },
        error: (error) => {
          console.error('Error loading nested project hierarchy:', error);
          this.dataSource.data = [];
          this.projectsLoaded = true;
          this.toastNotificationService.showNotification({
            message: 'Failed to load folder structure. Please try again.',
            type: 'error'
          });
          this.cd.detectChanges();
        }
      });
  }
}
